import {noop} from '@benchling/js-utils';

import {TableEditableProps} from 'notebook/tables/core-ui/TableEditableProps';
import * as TableConstants from 'notebook/tables/TableConstants';

/**
 * Default/stub values for TableEditableProps.
 *
 * Useful for contexts where you need to provide a complete TableEditableProps object
 * but only care about a subset of the props (e.g., when using TableModelProvider directly
 * without going through TableEditable).
 *
 */
export const TABLE_EDITABLE_DEFAULT_PROPS: Omit<TableEditableProps, 'table' | 'computedTable'> = {
  // Identity props
  cid: '',
  fileId: '',
  index: 0,
  isTableNameUsed: () => false,

  // Evaluation props
  tableNamespace: '',
  staleRows: [],
  errorRows: [],
  warningRows: [],
  cellColors: undefined,

  // State management
  storeState: noop,
  restoreState: () => false,
  metadataForRestore: null,

  // Navigation & selection
  setSelectionAbove: noop,
  setSelectionBelow: noop,
  requestSelection: noop,
  tabHandler: noop,

  // Modification handlers
  updateHandler: noop,
  deleteHandler: noop,
  insertItemHandler: noop,
  moveHandler: noop,

  // Multi-editing
  setMultiEditingForTable: noop,
  multiEditing: false,
  tryInsert: () => false,
  canInsert: () => false,

  // Mouse handlers
  mouseDownHandler: noop,
  mouseOverHandler: noop,

  // Display props
  viewer: false,
  showExcessLabels: false,
  showTableRowExcessLabels: false,
  showFormulaBox: false,
  dirty: false,
  indentation: 0,
  isMaximized: false,
  isSplitScreen: false,
  isFocused: false,
  isCollapsible: false,
  isCompactBottomSpacing: false,
  isLeftAlignColumnHeader: false,
  isLeftAlignRowHeader: false,
  isNestedInParentComponent: false,
  isTableHeaderFaded: false,
  hasPlaceholderRows: false,
  forceCollapsed: false,
  disableLegacyHeader: false,
  disableMaximizedTables: false,

  // Capabilities
  canRename: false,
  canAddRemoveColumns: false,
  canAddColumnsFreeform: false,
  canAttachFileToCell: () => false,
  disableFormulas: false,

  // Locking
  locked: false,
  getEditWhileCellLockedWarning: () => '',
  editWhileTableLockedWarning: null,

  // Dimensions & reordering
  allowReorderDimensions: {
    [TableConstants.TableDimension.ROW]: true,
    [TableConstants.TableDimension.COLUMN]: true,
  },

  // Clipboard
  setClipboard: noop,

  // Maximized/split screen
  setMaximizedTable: noop,

  // Selectors
  selectorsEnabled: false,
  selectorsLink: {value: [], selectorUsageCounts: {}},
  openManageSelectorsModal: noop,

  // Search
  searchResultsTree: null,
  selectedSearchResultTree: null,
};
